import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Shield, Mail, Phone, Key, Copy, Download, Save, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AdminSecuritySettings = () => {
  const [settings, setSettings] = useState({
    recovery_email: '',
    recovery_phone: '',
    two_factor_enabled: false,
    two_factor_method: 'email',
    backup_codes_count: 0
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin-security/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load security settings:', error);
      console.warn('Security settings not configured yet');
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    // Validation
    if (settings.two_factor_enabled) {
      if ((settings.two_factor_method === 'email' || settings.two_factor_method === 'both') && !settings.recovery_email) {
        toast.error('Recovery email is required for email 2FA');
        return;
      }
      if ((settings.two_factor_method === 'sms' || settings.two_factor_method === 'both') && !settings.recovery_phone) {
        toast.error('Recovery phone is required for SMS 2FA');
        return;
      }
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      await axios.put(
        `${API_URL}/admin-security/settings`,
        settings,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Security settings saved successfully');
      loadSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateBackupCodes = async () => {
    if (!window.confirm('Generate new backup codes? This will invalidate any existing codes.')) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin-security/generate-backup-codes`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setBackupCodes(response.data.codes);
      setShowBackupCodes(true);
      toast.success('Backup codes generated! Save them securely.');
      loadSettings();
    } catch (error) {
      console.error('Failed to generate backup codes:', error);
      toast.error('Failed to generate backup codes');
    }
  };

  const handleCopyBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    toast.success('Backup codes copied to clipboard');
  };

  const handleDownloadBackupCodes = () => {
    const codesText = backupCodes.join('\n');
    const blob = new Blob([`Admin Account Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\n\n${codesText}\n\nKeep these codes safe! Each can only be used once.`], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Backup codes downloaded');
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <Shield className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Admin Account Security</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Enhance your admin account security with recovery options and two-factor authentication.
      </p>

      {/* Recovery Contacts */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Recovery Contacts</h3>
        <p className="text-sm text-gray-600 mb-4">
          These will be used to recover your account if you forget your password or lose access.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Mail size={16} className="inline mr-2" />
              Recovery Email
            </label>
            <input
              type="email"
              value={settings.recovery_email || ''}
              onChange={(e) => setSettings({ ...settings, recovery_email: e.target.value })}
              className="input"
              placeholder="recovery@example.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use a different email than your primary login email
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Phone size={16} className="inline mr-2" />
              Recovery Phone
            </label>
            <input
              type="tel"
              value={settings.recovery_phone || ''}
              onChange={(e) => setSettings({ ...settings, recovery_phone: e.target.value })}
              className="input"
              placeholder="+1234567890"
            />
            <p className="text-xs text-gray-500 mt-1">
              Include country code (e.g., +1 for US)
            </p>
          </div>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Two-Factor Authentication (2FA)</h3>
        <p className="text-sm text-gray-600 mb-4">
          Add an extra layer of security by requiring a verification code when logging in.
        </p>

        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="two_factor_enabled"
              checked={settings.two_factor_enabled}
              onChange={(e) => setSettings({ ...settings, two_factor_enabled: e.target.checked })}
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <label htmlFor="two_factor_enabled" className="text-sm font-medium text-gray-700">
              Enable Two-Factor Authentication
            </label>
          </div>

          {settings.two_factor_enabled && (
            <div className="ml-7 space-y-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Method
              </label>
              <div className="space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="email"
                    checked={settings.two_factor_method === 'email'}
                    onChange={(e) => setSettings({ ...settings, two_factor_method: e.target.value })}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Email (send code to recovery email)</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="sms"
                    checked={settings.two_factor_method === 'sms'}
                    onChange={(e) => setSettings({ ...settings, two_factor_method: e.target.value })}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">SMS (send code to recovery phone)</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="radio"
                    value="both"
                    checked={settings.two_factor_method === 'both'}
                    onChange={(e) => setSettings({ ...settings, two_factor_method: e.target.value })}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm">Both (choose at login)</span>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backup Codes */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4">Backup Codes</h3>
        <p className="text-sm text-gray-600 mb-4">
          Generate one-time use backup codes for emergency access if you lose your recovery contacts.
        </p>

        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={handleGenerateBackupCodes}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <Key size={18} />
            <span>Generate New Backup Codes</span>
          </button>
          {settings.backup_codes_count > 0 && (
            <span className="text-sm text-gray-600">
              {settings.backup_codes_count} unused codes available
            </span>
          )}
        </div>

        {showBackupCodes && backupCodes && (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
            <div className="flex items-start space-x-3 mb-3">
              <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-1" size={20} />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-1">Save These Codes Now!</h4>
                <p className="text-sm text-yellow-800">
                  These codes will only be shown once. Save them in a secure location. Each code can only be used once.
                </p>
              </div>
            </div>

            <div className="bg-white rounded p-4 mb-3 font-mono text-sm">
              {backupCodes.map((code, index) => (
                <div key={index} className="py-1">
                  {index + 1}. {code}
                </div>
              ))}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handleCopyBackupCodes}
                className="btn btn-secondary text-sm flex items-center space-x-1"
              >
                <Copy size={14} />
                <span>Copy</span>
              </button>
              <button
                onClick={handleDownloadBackupCodes}
                className="btn btn-secondary text-sm flex items-center space-x-1"
              >
                <Download size={14} />
                <span>Download</span>
              </button>
              <button
                onClick={() => setShowBackupCodes(false)}
                className="btn btn-secondary text-sm"
              >
                I've Saved Them
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <button
        onClick={handleSaveSettings}
        disabled={saving}
        className="btn btn-primary flex items-center space-x-2"
      >
        <Save size={18} />
        <span>{saving ? 'Saving...' : 'Save Security Settings'}</span>
      </button>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-900 mb-2">How Admin Recovery Works:</h3>
        <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
          <li>On login page, click "Admin Recovery" button</li>
          <li>Enter your admin email</li>
          <li>Choose recovery method (email, SMS, or backup code)</li>
          <li>Enter verification code sent to your recovery contact</li>
          <li>Reset your password and regain access</li>
        </ol>
        <p className="text-xs text-blue-700 mt-3">
          <strong>Security Tip:</strong> Use a recovery email you control that's different from your login email. Store backup codes in a password manager or secure location.
        </p>
      </div>
    </div>
  );
};

export default AdminSecuritySettings;
